<script>
import { useEotlCore } from '@/store/eotlStore'
import { inject, onActivated, onBeforeMount, onDeactivated, onMounted, provide, reactive, ref, watchEffect } from 'vue';

const ALERT_CONTEXT_KEY = Symbol('eotl-core-alert')

const DEFAULT_CONTEXT = reactive({
    childAlertRoots: 0
})

/**
 * Apps like ui-ordering and ui-inventory can be run standalone or within another app which has multiple alert containers.
 * To avoid duplicate alerts, this method will provide a context saying that parent alert containers shouldn't be shown.
 */
export function createAlertContext() {
    provide(ALERT_CONTEXT_KEY, reactive({
        // a counter to record whether there are <Alert> components deeper in the tree
        // which should be used instead of the current one
        childAlertRoots: 0
    }))

    // Get the parent alert context
    const parent = inject(ALERT_CONTEXT_KEY, DEFAULT_CONTEXT)

    // record the current component's mount and activation state
    const isMounted = ref(false)
    const isActivated = ref(true)
    onMounted(() => isMounted.value = true)
    onBeforeMount(() => isMounted.value = false)
    onActivated(() => isActivated.value = true)
    onDeactivated(() => isActivated.value = false)

    // while this component is mounted and activated (under <keep-alive>), increment the parent's counter
    watchEffect((onCleanup) => {
        if (!isActivated.value || !isMounted.value) return

        parent.childAlertRoots++
        useEotlCore().alert.show = false

        onCleanup(() => {
            parent.childAlertRoots--
            useEotlCore().alert.show = false
        })
    })
}
</script>

<script setup>
const eotlStore = useEotlCore()
const alertRoot = inject(ALERT_CONTEXT_KEY, DEFAULT_CONTEXT)
</script>

<template>
    <div>
        <div v-if="eotlStore.alert.show && !alertRoot?.childAlertRoots"
            :class="'mb-3 alert alert-dismissable alert-' + eotlStore.alert.style" role="alert">
            <button type="button" class="close" data-dismiss="alert" @click="() => eotlStore.alertReset()" aria-label="Close">
                ×
            </button>
            <h4>{{ eotlStore.alert.title }}</h4>
            <p class="mb-0">
                {{ eotlStore.alert.message }}
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped></style>
